import React, { useEffect } from "react";
import "./fileuploadStyles.scss";
import { Chip, Stack, Tooltip } from "@mui/material";
import { AlertDialog } from "../../common/AlertDailog";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { protocolUploadSchema } from "../../../config/schemas/protocolUploadSchema";
import { useProtocol } from "../../../hooks/useProtocol";
import { useSnackbar } from "../../../hooks/useSnackbar";
import { useParams } from "react-router-dom";
import FormFields from "./uploadFields.json";
import DynamicForm from "../../common/DynamicForm";
import { hideLoader, showLoader } from "../../../store/slices/loaderSlice";
import { useAppDispatch } from "../../../store";
import UploadFileComponent from "../../common/UploadFileComponent";

const acceptedMimeTypes = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf",
];

const UploadProtocolDialog: React.FunctionComponent<any> = (props) => {
  const {
    openUploadProtocol,
    setOpenUploadProtocol,
    getAllProtocolFunc,
    file,
    setFile,
    isEditProtocol,
    setIsEditProtocol,
    selectedProtocol,
    setSelectedProtocol,
    copyProtocol,
    isUploaded,
    setIsUploaded,
  } = props;
  const {
    uploadProtocol,
    uploadProtocolData,
    uploadProtocolIsError,
    uploadProtocolError,
    uploadProtocolIsSuccess,
    updateProtocolsLoading,
    updateProtocol,
    updateProtocolData,
    updateProtocolIsError,
    updateProtocolError,
    updateProtocolIsSuccess,
    uploadProtocolsLoading,
  } = useProtocol();
  const { showSuccess, handleError, showError } = useSnackbar();
  const params = useParams();
  const dispatch = useAppDispatch();
  const values = isEditProtocol && {
    ...selectedProtocol,
    protocol_name: selectedProtocol?.protocol_name,
  };
  type FormValues = z.infer<typeof protocolUploadSchema>;
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(protocolUploadSchema),
    defaultValues: {},
    values,
  });

  const onInvalid = (errors: any) => console.debug(errors);
  const submit = (obj: any) => {
    if (!isUploaded) {
      showError("Please attach a file");
      return;
    }
    const { protocol_name } = obj;
    if (isEditProtocol) {
      const payload = {
        protocol_uuid: copyProtocol?.id,
        protocol_name,
        ...(file && { data: file }),
      };
      updateProtocol(payload);
    } else {
      uploadProtocol({
        protocol_name,
        study_id: params?.study_id,
        data: file,
      });
    }
  };

  const handleDeleteChip = () => {
    setFile(null);
    setSelectedProtocol({});
    setIsUploaded(false);
  };
  const handleCloseUploadProtocol = () => {
    setFile(null);
    reset();
    setSelectedProtocol({});
    setOpenUploadProtocol(false);
    setIsEditProtocol(false);
    setIsUploaded(false);
  };

  useEffect(() => {
    if (uploadProtocolsLoading || updateProtocolsLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [uploadProtocolsLoading, updateProtocolsLoading]);

  useEffect(() => {
    if (uploadProtocolIsSuccess || updateProtocolIsSuccess) {
      const successMsg = uploadProtocolData ?? updateProtocolData;
      getAllProtocolFunc();
      showSuccess(successMsg?.details, "protocol-upload-success");
      handleCloseUploadProtocol();
    }
  }, [uploadProtocolIsSuccess, updateProtocolIsSuccess]);

  useEffect(() => {
    if (uploadProtocolIsError || updateProtocolIsError) {
      const errorMsg: any = uploadProtocolError ?? updateProtocolError;
      handleError(errorMsg);
    }
  }, [uploadProtocolIsError, updateProtocolIsError]);
  return (
    <>
      <AlertDialog
        open={openUploadProtocol}
        title="Upload Protocol"
        handleClose={handleCloseUploadProtocol}
        handleConfirm={handleSubmit(submit, onInvalid)}
        maxWidth="sm"
      >
        <UploadFileComponent
          acceptedMimeTypes={acceptedMimeTypes}
          maxFileSize={50}
          fileformats={[".pdf", ".doc", ".docx"]}
          setIsUploaded={setIsUploaded}
          selectedFileData={selectedProtocol}
          file={file}
          setFile={setFile}
        />
        <form onSubmit={handleSubmit(submit, onInvalid)}>
          <DynamicForm
            fieldsJson={FormFields?.uploadFields}
            errors={errors}
            register={register}
            control={control}
          />
        </form>
        {(file || selectedProtocol?.filename) && (
          <Stack
            direction="row"
            rowGap={1}
            columnGap={1}
            mb={-2.5}
            className="flexWrap"
          >
            <Chip
              size="small"
              label={
                <Tooltip
                  title={
                    selectedProtocol?.filename?.length > 80 ||
                    file?.name?.length > 80
                      ? selectedProtocol?.filename ?? file?.name
                      : null
                  }
                  placement="bottom-start"
                >
                  {selectedProtocol?.filename
                    ? selectedProtocol?.filename
                    : file.name}
                </Tooltip>
              }
              variant="outlined"
              color="primary"
              onDelete={handleDeleteChip}
              className="fw500"
            />
          </Stack>
        )}
      </AlertDialog>
    </>
  );
};

export default UploadProtocolDialog;
